import React, { useState, useRef, useEffect } from 'react';
import { ActionIcon, Textarea, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconMessageCircle, IconX, IconSend, IconPaperclip, IconFile, IconTrash } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthProvider';
import Environment from '../utility/environment';
import './ChatWidget.css';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  files?: string[];
}

const CHAT_STORAGE_PREFIX = 'aplus.chat.v1';
const CHAT_POS_STORAGE_KEY = 'aplus.chat.fab.pos.v1';
const CHAT_FAB_SIZE = 52;
const CHAT_BOX_WIDTH = 380;
const CHAT_BOX_HEIGHT = 520;
const CHAT_DRAG_THRESHOLD = 5;
const CHAT_FAB_MARGIN = 24;

type FabPos = { x: number; y: number };

function loadFabPos(): FabPos | null {
  try {
    const raw = localStorage.getItem(CHAT_POS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
  } catch { /* ignore */ }
  return null;
}

function clampFabPos(pos: FabPos): FabPos {
  const maxX = Math.max(0, window.innerWidth - CHAT_FAB_SIZE);
  const maxY = Math.max(0, window.innerHeight - CHAT_FAB_SIZE);
  return {
    x: Math.min(Math.max(0, pos.x), maxX),
    y: Math.min(Math.max(0, pos.y), maxY),
  };
}

function defaultFabPos(): FabPos {
  return {
    x: window.innerWidth - CHAT_FAB_SIZE - CHAT_FAB_MARGIN,
    y: window.innerHeight - CHAT_FAB_SIZE - CHAT_FAB_MARGIN,
  };
}

function computeBoxPos(fab: FabPos): { left: number; top: number } {
  const openRight = fab.x + CHAT_FAB_SIZE / 2 < window.innerWidth / 2;
  const openBelow = fab.y + CHAT_FAB_SIZE / 2 < window.innerHeight / 2;
  const left = openRight
    ? Math.min(fab.x + CHAT_FAB_SIZE + 8, window.innerWidth - CHAT_BOX_WIDTH - 8)
    : Math.max(8, fab.x - CHAT_BOX_WIDTH - 8);
  const top = openBelow
    ? Math.min(fab.y, window.innerHeight - CHAT_BOX_HEIGHT - 8)
    : Math.max(8, fab.y + CHAT_FAB_SIZE - CHAT_BOX_HEIGHT);
  return { left, top };
}

function storageKeyFor(userId: string | undefined, enterpriseId: string | undefined): string | null {
  if (!userId) return null;
  return `${CHAT_STORAGE_PREFIX}.${userId}.${enterpriseId ?? 'noent'}`;
}

function loadHistory(key: string | null): ChatMessage[] {
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage =>
        m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string'
    );
  } catch {
    return [];
  }
}

const IMAGE_EXTS = /\.(png|jpg|jpeg|gif|webp)$/i;
const FILE_URL_RE = /(?:^|\s)(\/static\/chat_(?:files|charts)\/[^\s)]+)/g;

function renderAssistantContent(content: string) {
  if (!FILE_URL_RE.test(content)) return content || null;

  FILE_URL_RE.lastIndex = 0;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;

  for (const match of content.matchAll(FILE_URL_RE)) {
    const url = match[1];
    const fullMatch = match[0];
    const startIdx = match.index! + (fullMatch.length - url.length);

    if (startIdx > lastIdx) {
      parts.push(content.slice(lastIdx, startIdx));
    }

    const absoluteUrl = `${Environment.VITE_API_URL}${url}`;
    if (IMAGE_EXTS.test(url)) {
      parts.push(
        <img
          key={startIdx}
          src={absoluteUrl}
          alt="chart"
          className="chat-inline-image"
        />
      );
    } else {
      const name = url.split('/').pop() || 'file';
      parts.push(
        <a
          key={startIdx}
          href={absoluteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-download-link"
        >
          <IconFile size={14} /> {name}
        </a>
      );
    }
    lastIdx = startIdx + url.length;
  }

  if (lastIdx < content.length) {
    parts.push(content.slice(lastIdx));
  }

  return <>{parts}</>;
}

const ChatWidget: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { accessToken, user, actualEnterprise } = useAuth();
  const storageKey = storageKeyFor(user?.id, actualEnterprise?.id);
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadHistory(storageKey));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRunIdRef = useRef(0);

  const [fabPos, setFabPos] = useState<FabPos>(() => clampFabPos(loadFabPos() ?? defaultFabPos()));
  const dragStateRef = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    const onResize = () => setFabPos((cur) => clampFabPos(cur));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleFabPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const target = e.currentTarget;
    dragStateRef.current = {
      pointerId: e.pointerId,
      offsetX: e.clientX - fabPos.x,
      offsetY: e.clientY - fabPos.y,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    target.setPointerCapture(e.pointerId);
  };

  const handleFabPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < CHAT_DRAG_THRESHOLD) return;
    drag.moved = true;
    setFabPos(clampFabPos({ x: e.clientX - drag.offsetX, y: e.clientY - drag.offsetY }));
  };

  const handleFabPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const target = e.currentTarget;
    if (target.hasPointerCapture(e.pointerId)) target.releasePointerCapture(e.pointerId);
    dragStateRef.current = null;
    if (drag.moved) {
      try { localStorage.setItem(CHAT_POS_STORAGE_KEY, JSON.stringify(fabPos)); } catch { /* ignore */ }
    } else {
      setOpen((v) => !v);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      pollRunIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    setMessages(loadHistory(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (messages.length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(messages));
      }
    } catch {
      // storage full or disabled — fall through silently
    }
  }, [messages, storageKey]);

  const enterpriseQuery = actualEnterprise?.id
    ? `?enterprise_id=${encodeURIComponent(actualEnterprise.id)}`
    : '';

  const clearHistory = async () => {
    setMessages([]);
    if (storageKey) {
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    }
    try {
      await fetch(`${Environment.VITE_API_URL}/api/v1/chat${enterpriseQuery}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
      });
    } catch (err) {
      console.error('Failed to clear server-side chat session:', err);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachedFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && attachedFiles.length === 0) || loading) return;

    const fileNames = attachedFiles.map((f) => f.name);
    const userMsg: ChatMessage = { role: 'user', content: text, files: fileNames.length ? fileNames : undefined };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    const filesToSend = [...attachedFiles];
    setAttachedFiles([]);
    setLoading(true);

    const pollRunId = (pollRunIdRef.current += 1);

    try {
      const formData = new FormData();
      formData.append('message', text || '(see attached files)');
      if (actualEnterprise?.id) {
        formData.append('enterprise_id', actualEnterprise.id);
      }
      for (const file of filesToSend) {
        formData.append('files', file);
      }

      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
        body: formData,
      });

      if (response.status === 409) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content:
              'Still working on your previous message. Wait until it finishes, then send again.',
          },
        ]);
        return;
      }

      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `Chat request failed (${response.status})`);
      }

      const submitPayload: { job_id: string; poll_interval_ms?: number } = await response.json();
      const jobId = submitPayload.job_id;
      const pollMs = submitPayload.poll_interval_ms ?? 60_000;

      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      const sleep = (ms: number) =>
        new Promise<void>((resolve) => {
          globalThis.setTimeout(resolve, ms);
        });

      while (true) {
        if (pollRunIdRef.current !== pollRunId) {
          return;
        }

        const statusResponse = await fetch(
          `${Environment.VITE_API_URL}/api/v1/chat/jobs/${jobId}${enterpriseQuery}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          },
        );

        if (!statusResponse.ok) {
          throw new Error(`Status poll failed (${statusResponse.status})`);
        }

        const body: { status: string; content: string; error: string | null } = await statusResponse.json();

        if (body.status === 'error') {
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
              updated[updated.length - 1] = {
                role: 'assistant',
                content: body.error || 'An error occurred.',
              };
            }
            return updated;
          });
          break;
        }

        if (body.status === 'done') {
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
              updated[updated.length - 1] = { role: 'assistant', content: body.content };
            }
            return updated;
          });
          break;
        }

        const placeholder =
          body.content ||
          (body.status === 'pending' || body.status === 'running' ? '…' : '');
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === 'assistant') {
            updated[updated.length - 1] = { role: 'assistant', content: placeholder };
          }
          return updated;
        });

        await sleep(pollMs);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === '') {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: 'Sorry, something went wrong.',
          };
          return updated;
        }
        return [...prev, { role: 'assistant', content: 'Sorry, something went wrong.' }];
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {open && (
        <div
          className="chat-widget-box"
          style={(() => {
            const { left, top } = computeBoxPos(fabPos);
            return { left, top, right: 'auto', bottom: 'auto' };
          })()}
        >
          <div className="chat-widget-header">
            <span>{t('chat.assistant')}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {messages.length > 0 && (
                <Tooltip label={t('chat.clearHistory')} withArrow>
                  <ActionIcon
                    variant="transparent"
                    onClick={clearHistory}
                    size="sm"
                    disabled={loading}
                  >
                    <IconTrash size={16} color="#fff" />
                  </ActionIcon>
                </Tooltip>
              )}
              <ActionIcon variant="transparent" onClick={() => setOpen(false)} size="sm">
                <IconX size={16} color="#fff" />
              </ActionIcon>
            </div>
          </div>

          <div className="chat-widget-messages">
            {messages.length === 0 && (
              <div className="chat-widget-empty">{t('chat.empty')}</div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`chat-bubble chat-bubble-${msg.role}`}>
                {msg.role === 'assistant'
                  ? renderAssistantContent(msg.content) || (loading ? '...' : '')
                  : msg.content || ''}
                {msg.files && msg.files.length > 0 && (
                  <div className="chat-file-chips">
                    {msg.files.map((name, fi) => (
                      <span key={fi} className="chat-file-chip-sent">
                        <IconFile size={12} /> {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {attachedFiles.length > 0 && (
            <div className="chat-file-previews">
              {attachedFiles.map((file, i) => (
                <span key={i} className="chat-file-chip">
                  {file.type.startsWith('image/') ? (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="chat-file-thumb"
                    />
                  ) : (
                    <IconFile size={14} />
                  )}
                  <span className="chat-file-name">{file.name}</span>
                  <IconX size={12} className="chat-file-remove" onClick={() => removeFile(i)} />
                </span>
              ))}
            </div>
          )}

          <div className="chat-widget-input">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.csv,.xlsx,.xls,.txt"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <ActionIcon
              onClick={() => fileInputRef.current?.click()}
              size="lg"
              variant="subtle"
              disabled={loading}
              style={{ alignSelf: 'flex-end' }}
            >
              <IconPaperclip size={18} />
            </ActionIcon>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chat.placeholder')}
              autosize
              minRows={1}
              maxRows={4}
              style={{ flex: 1 }}
              disabled={loading}
            />
            <ActionIcon
              onClick={sendMessage}
              disabled={loading || (!input.trim() && attachedFiles.length === 0)}
              size="lg"
              variant="filled"
              style={{ backgroundColor: '#30715B', marginLeft: 8, alignSelf: 'flex-end' }}
            >
              <IconSend size={18} />
            </ActionIcon>
          </div>
        </div>
      )}

      <button
        className="chat-widget-fab"
        style={{ left: fabPos.x, top: fabPos.y, right: 'auto', bottom: 'auto', touchAction: 'none' }}
        title={t('chat.fabTitle')}
        onPointerDown={handleFabPointerDown}
        onPointerMove={handleFabPointerMove}
        onPointerUp={handleFabPointerUp}
        onPointerCancel={handleFabPointerUp}
      >
        {open ? <IconX size={24} /> : <IconMessageCircle size={24} />}
      </button>
    </>
  );
};

export default ChatWidget;
